import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { convertLanguage } from '@/components/Functional/utils/convertLanguage'
import type { CampaignData } from '@/schemas/v2/campaign'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

// キャンペーンに応じてデフォルトの言語を選ぶようにするhook
// i18nのライブラリが`i18nextLng`というキーでローカルストレージに保存するようになっている。
// callbackで`{campaign-name}_i18nextLng`というキーでローカルストレージに追加で保存するようにもしている。
// そのため、APIなどの実行に利用されるのは、`{campaign-name}_i18nextLng`だが、実際の切り替えに寄与しているのは`i18nextLng`であることに注意
//
// 流れ
// 1. キャンペーンに応じた言語設定がローカルストレージに存在しない場合（= 初回アクセス）
//    1.1. ブラウザの言語設定を取得し、変換する
//    1.2. 変換した言語設定を`{campaign-name}_i18nextLng`に保存する
//    1.3. 変換した言語設定を`i18nextLng`に保存する
// 2. キャンペーンに応じた言語設定がローカルストレージに存在する場合（= 以前アクセス済み & 言語設定済み）
//    2.1. その言語設定を`i18nextLng`に保存する
// 3. キャンペーンが多言語化対応していない場合
//    3.1. `ja_JP`を`{campaign-name}_i18nextLng`に保存する
//    3.2. `ja_JP`を`i18nextLng`に保存する
export const useChangeLanguage = ({ campaign }: { campaign?: CampaignData | null }): { language: string } => {
  const { i18n } = useTranslation()
  const { LOCAL_STORAGE_I18N_KEY } = useCampaignSlug()

  // i18n.changeLanguage と localStorage への書き込みは副作用として useEffect で行う
  useEffect(() => {
    if (!campaign) return

    const hasMultipleLanguages = campaign.language.length > 1
    const isLocalizedCampaign = campaign.isLocalize ?? hasMultipleLanguages

    if (isLocalizedCampaign && hasMultipleLanguages) {
      // 多言語化対応している場合
      const detectedLng = convertLanguage(navigator.language)
      const campaignSpecificI18nextLng = localStorage.getItem(LOCAL_STORAGE_I18N_KEY)
      if (!campaignSpecificI18nextLng) {
        // キャンペーンに応じた多言語の設定がない場合にはブラウザのデフォルトを設定
        localStorage.setItem(LOCAL_STORAGE_I18N_KEY, detectedLng)
        i18n.changeLanguage(detectedLng)
      } else {
        // キャンペーンに応じた多言語の設定がある場合には、そのまま設定
        i18n.changeLanguage(campaignSpecificI18nextLng)
      }
    } else {
      // 多言語化対応していない場合、強制的に日本語に設定する
      localStorage.setItem(LOCAL_STORAGE_I18N_KEY, 'ja_JP')
      i18n.changeLanguage('ja_JP')
    }
  }, [campaign, i18n, LOCAL_STORAGE_I18N_KEY])

  // i18n.language は changeLanguage() 呼び出し後に自動更新されるため、直接返す
  return { language: i18n.language }
}
